import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CalendarDays, Star, MailOpen, CheckCircle2, Newspaper, Landmark, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { postForm } from "@/lib/api";
import { PRIORITIES, PRESS, EVENTS, CHECK_INFO, SLOGAN } from "@/lib/data";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Chapter } from "@/components/Chapter";
import SloganMarquee from "@/components/SloganMarquee";
import StripeBar from "@/components/StripeBar";

const JoinForm = () => {
  const [form, setForm] = useState({ name: "", email: "", zip_code: "", company: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postForm("/campaign/subscribe", form);
      setDone(true);
      toast.success("You're on the team. Welcome aboard!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-3 bg-cream/10 border border-cream/20 rounded-xl px-5 py-4" data-testid="join-success-message">
        <CheckCircle2 className="w-6 h-6 text-gold shrink-0" />
        <p className="text-cream font-semibold">You're in! Watch your inbox for campaign updates.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="join-campaign-form">
      <input type="text" name="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <input
        required
        type="text"
        placeholder="Full name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        data-testid="join-campaign-name-input"
        className="w-full bg-white/95 text-navy rounded-lg px-4 py-3.5 text-base font-medium placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-gold"
      />
      <input
        required
        type="email"
        placeholder="Email address"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        data-testid="join-campaign-email-input"
        className="w-full bg-white/95 text-navy rounded-lg px-4 py-3.5 text-base font-medium placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-gold"
      />
      <div className="flex gap-3">
        <input
          type="text"
          placeholder="ZIP"
          value={form.zip_code}
          onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
          data-testid="join-campaign-zip-input"
          className="w-24 sm:w-28 bg-white/95 text-navy rounded-lg px-4 py-3.5 text-base font-medium placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-gold"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="join-campaign-submit-button"
          className="flex-1 bg-navy hover:bg-navy-light disabled:opacity-60 text-white font-display font-extrabold uppercase tracking-wider text-lg rounded-lg px-5 transition-all duration-200 hover:scale-[1.02] min-h-[52px]"
        >
          {busy ? "Joining…" : "Join the Campaign"}
        </button>
      </div>
    </form>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const watermarkY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative bg-navy overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: watermarkY }} className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <div className="absolute -bottom-8 left-0 font-display font-black uppercase text-[26vw] leading-none text-outline-cream whitespace-nowrap">
          GRAHAM
        </div>
      </motion.div>
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-20 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-y-0 right-8 w-2 bg-crimson" />
        <div className="absolute inset-y-0 right-16 w-2 bg-cream/60" />
        <div className="absolute inset-y-0 right-24 w-2 bg-royal" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal y={14}>
            <p className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-gold mb-6 flex items-center gap-3" data-testid="hero-office-line">
              <Star className="w-3.5 h-3.5 fill-gold" /> Republican Candidate · Delaware Attorney General
            </p>
          </Reveal>
          <h1 className="font-display font-black uppercase tracking-tight leading-[0.92] text-cream text-5xl sm:text-6xl lg:text-7xl" data-testid="hero-primary-message">
            <MaskedLine delay={0.15}>Protecting</MaskedLine>
            <MaskedLine delay={0.27}>Delawareans.</MaskedLine>
            <MaskedLine delay={0.39} className="text-crimson">Restoring</MaskedLine>
            <MaskedLine delay={0.51} className="text-crimson">Accountability.</MaskedLine>
          </h1>
          <Reveal delay={0.75} className="mt-7 max-w-xl">
            <p className="text-cream/75 text-base sm:text-lg leading-relaxed" data-testid="hero-intro-text">
              Dave Graham — U.S. Army Sergeant, retired senior executive, and working-class Delawarean — is running for Attorney General to keep families safe and make Dover answer to the people again.
            </p>
          </Reveal>
          <Reveal delay={0.9} className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/meet-dave"
              data-testid="hero-meet-dave-button"
              className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-dark text-white font-display font-extrabold uppercase tracking-wider text-xl px-9 py-4 rounded-full transition-all duration-200 hover:scale-105 shadow-lg shadow-crimson/30 min-h-[56px]"
            >
              Meet Dave <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contribute"
              data-testid="hero-contribute-button"
              className="inline-flex items-center gap-2 border-2 border-cream/40 hover:border-cream text-cream font-display font-extrabold uppercase tracking-wider text-xl px-9 py-4 rounded-full transition-all duration-200 hover:bg-cream/5 min-h-[56px]"
            >
              Contribute
            </Link>
          </Reveal>
          <Reveal delay={1.05} className="mt-8">
            <p className="font-editorial font-semibold text-gold text-sm sm:text-base tracking-wide" data-testid="hero-slogan">
              {SLOGAN}
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: photoY }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} className="relative max-w-md mx-auto">
            <div className="absolute -top-4 -left-4 right-8 bottom-8 border-2 border-gold/50 rounded-lg" aria-hidden="true" />
            <div className="absolute -bottom-4 -right-4 left-8 top-8 bg-crimson rounded-lg" aria-hidden="true" />
            <img
              src="/assets/dave-portrait.png"
              alt="Dave Graham, Republican candidate for Delaware Attorney General"
              data-testid="hero-candidate-photo"
              className="relative w-full h-auto rounded-lg shadow-2xl shadow-navy-dark object-cover"
            />
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-cream text-navy font-display font-extrabold uppercase tracking-wider text-base sm:text-lg px-6 py-2.5 rounded-full shadow-lg whitespace-nowrap">
              Dave Graham · For AG
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const EventBand = () => {
  const ev = EVENTS[0];
  return (
    <section className="bg-white border-b-4 border-crimson" data-testid="event-announcement-band">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        <div className="flex items-center gap-4 flex-1">
          <div className="bg-crimson/10 rounded-full p-3 shrink-0">
            <CalendarDays className="w-7 h-7 text-crimson" />
          </div>
          <div>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-crimson mb-1">See Dave in person</p>
            <p className="font-display font-extrabold uppercase tracking-wide text-navy text-xl sm:text-2xl leading-tight">
              {ev.title}
            </p>
            <p className="text-navy/60 text-sm sm:text-base font-semibold mt-0.5">
              {ev.date} · {ev.time}
            </p>
          </div>
        </div>
        <a
          href={ev.registerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="event-register-button"
          className="inline-flex items-center justify-center gap-2 border-2 border-crimson text-crimson hover:bg-crimson hover:text-white font-display font-extrabold uppercase tracking-wider text-lg px-7 py-3 rounded-full transition-colors duration-200 min-h-[52px] shrink-0"
        >
          {ev.registerLabel} <ArrowUpRight className="w-5 h-5" />
        </a>
      </div>
    </section>
  );
};

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [location]);

  return (
    <main data-testid="home-page">
      <Hero />
      <SloganMarquee />
      <EventBand />

      <section className="bg-cream py-16 sm:py-24 md:py-28" data-testid="bio-snapshot-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter kicker="Meet Dave" title="A Sergeant's Discipline. An Executive's Record. A Neighbor's Heart." />
          <div className="grid lg:grid-cols-12 gap-10 mt-10 items-start">
            <Reveal className="lg:col-span-5" delay={0.1}>
              <div className="relative bg-navy rounded-xl shadow-xl border-4 border-white px-8 py-14 text-center overflow-hidden" data-testid="bio-brand-panel">
                <StripeBar className="absolute top-0 inset-x-0 h-2.5" />
                <Star className="w-6 h-6 text-gold fill-gold mx-auto mb-6 mt-2" />
                <p className="font-display font-black uppercase text-cream leading-[0.9] text-5xl sm:text-6xl">
                  Dave<br />Graham
                </p>
                <StripeBar className="h-1 w-32 mx-auto my-6 rounded-full overflow-hidden" />
                <p className="font-display font-extrabold uppercase tracking-[0.2em] text-gold text-lg sm:text-xl">
                  For Attorney General
                </p>
                <p className="font-editorial font-semibold text-cream/80 text-sm sm:text-base mt-7 tracking-wide">
                  {SLOGAN}
                </p>
                <div className="mt-9 inline-flex items-center gap-2 border border-gold/60 text-gold font-mono text-xs tracking-[0.25em] uppercase px-5 py-2.5 rounded-full" data-testid="bio-veteran-badge">
                  U.S. Army Veteran
                </div>
                <StripeBar className="absolute bottom-0 inset-x-0 h-2.5" />
              </div>
            </Reveal>
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <p className="text-navy/80 text-base sm:text-lg leading-relaxed mb-6" data-testid="bio-intro-paragraph">
                  Dave Graham isn't a career politician. His experience in the military, business and the working world shaped his belief in discipline, responsibility and accountability. He's running for Attorney General to bring those principles to Delaware's Department of Justice and put public safety first.
                </p>
              </Reveal>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  ["U.S. Army Veteran", "E-5 Sergeant"],
                  ["Senior Executive", "Retired"],
                  ["CDL-A Driver", "Working Class"],
                  ["Goldey-Beacom College", "Graduate"],
                ].map(([a, b], i) => (
                  <Reveal key={a} delay={0.2 + i * 0.08}>
                    <div className="bg-white border-l-4 border-crimson rounded-r-lg px-5 py-4 shadow-sm" data-testid={`bio-badge-${i}`}>
                      <p className="font-display font-extrabold uppercase tracking-wide text-lg text-navy leading-tight">{a}</p>
                      <p className="font-mono text-xs uppercase tracking-widest text-navy/50 mt-1">{b}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.4}>
                <Link to="/meet-dave" data-testid="bio-read-more-link" className="inline-flex items-center gap-2 font-display font-extrabold uppercase tracking-wider text-lg text-crimson hover:text-crimson-dark transition-colors group">
                  Read Dave's story <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section id="priorities" className="bg-navy py-16 sm:py-24 md:py-28 scroll-mt-20" data-testid="priorities-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter title="Priorities for Delaware" dark />
          <Reveal delay={0.1} className="mt-5 max-w-2xl">
            <p className="text-cream/65 text-base sm:text-lg leading-relaxed">
              Five clear commitments Dave will bring to the Department of Justice — no fine print, no excuses.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {PRIORITIES.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.2 }} className="h-full bg-navy-light/40 border border-cream/10 rounded-xl p-6 sm:p-7" data-testid={`priority-card-${p.id}`}>
                  <div className="flex items-center justify-between mb-5">
                    <p.icon className="w-8 h-8 text-gold" />
                    <span className="font-mono text-xs text-cream/40 tracking-widest">0{i + 1}</span>
                  </div>
                  <h3 className="font-display font-extrabold uppercase tracking-wide text-xl sm:text-2xl text-cream leading-tight mb-3">{p.title}</h3>
                  <p className="text-cream/60 text-sm sm:text-base leading-relaxed">{p.summary}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-24 md:py-28" data-testid="press-highlight-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <Chapter kicker="News & Press" title="Delaware Is Talking" />
            <Reveal delay={0.15} className="mt-6">
              <p className="text-navy/75 text-base sm:text-lg leading-relaxed">
                Latest news and media coverage from Dave's campaign for Attorney General.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-6">
              <Link to="/news" data-testid="press-view-all-link" className="inline-flex items-center gap-2 font-display font-extrabold uppercase tracking-wider text-lg text-crimson hover:text-crimson-dark transition-colors group">
                All news & press <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-7" delay={0.2}>
            <a href={PRESS[0].url} target="_blank" rel="noopener noreferrer" data-testid="press-feature-card" className="block bg-white rounded-xl border border-navy/10 shadow-md hover:shadow-xl transition-shadow p-7 sm:p-9 group">
              <div className="flex items-center gap-3 mb-4">
                <Newspaper className="w-6 h-6 text-crimson" />
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-navy/50">{PRESS[0].source} · {PRESS[0].date}</span>
              </div>
              <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-navy leading-tight group-hover:text-crimson transition-colors mb-3">
                {PRESS[0].title}
              </h3>
              <p className="text-navy/70 text-base leading-relaxed">{PRESS[0].summary}</p>
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-crimson py-16 sm:py-24" data-testid="join-campaign-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="font-display font-black uppercase tracking-tight leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl">
                Join the Campaign
              </h2>
              <StripeBar className="h-1 w-28 mt-5 rounded-full overflow-hidden" />
              <p className="text-white/80 text-base sm:text-lg leading-relaxed mt-5 max-w-lg">
                Get campaign updates, event invitations, and ways to help delivered straight to your inbox. This campaign is powered by Delawareans like you.
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-6" delay={0.15}>
            <JoinForm />
          </Reveal>
        </div>
      </section>

      <section className="bg-navy-dark py-16 sm:py-24 md:py-28" data-testid="contribute-teaser-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter kicker="Support the Campaign" title="Two Ways to Contribute" dark />
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <Reveal delay={0.1}>
              <div className="h-full bg-navy-light/40 border border-cream/10 rounded-xl p-7 sm:p-9 flex flex-col" data-testid="contribute-teaser-online">
                <ShieldCheck className="w-9 h-9 text-gold mb-5" />
                <h3 className="font-display font-extrabold uppercase tracking-wide text-2xl sm:text-3xl text-cream mb-3">Contribute Online</h3>
                <p className="text-cream/65 text-base leading-relaxed mb-7 flex-1">
                  Your contribution helps Dave reach voters across Delaware and share his message.
                </p>
                <Link to="/contribute" data-testid="contribute-teaser-online-button" className="inline-flex justify-center items-center gap-2 bg-crimson hover:bg-crimson-dark text-white font-display font-extrabold uppercase tracking-wider text-lg px-8 py-4 rounded-full transition-all duration-200 hover:scale-[1.03] min-h-[56px]">
                  Give Online <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="h-full bg-cream text-navy rounded-xl p-7 sm:p-9 flex flex-col" data-testid="contribute-teaser-check">
                <MailOpen className="w-9 h-9 text-crimson mb-5" />
                <h3 className="font-display font-extrabold uppercase tracking-wide text-2xl sm:text-3xl mb-3">Contribute by Check</h3>
                <p className="text-navy/70 text-base leading-relaxed mb-5">
                  Prefer to mail a check? Make it payable to <strong>{CHECK_INFO.payableTo}</strong> and send it to:
                </p>
                <div className="bg-navy text-cream rounded-lg px-5 py-4 mb-7 font-semibold text-base leading-relaxed" data-testid="check-mailing-address-card">
                  {CHECK_INFO.payableTo}<br />{CHECK_INFO.poBox}<br />{CHECK_INFO.cityStateZip}
                </div>
                <Link to="/contribute" data-testid="contribute-teaser-check-button" className="mt-auto inline-flex justify-center items-center gap-2 border-2 border-navy hover:bg-navy hover:text-cream text-navy font-display font-extrabold uppercase tracking-wider text-lg px-8 py-4 rounded-full transition-colors duration-200 min-h-[56px]">
                  Check Instructions
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.3} className="mt-10">
            <p className="flex items-center justify-center gap-2 text-cream/40 font-mono text-xs uppercase tracking-widest text-center">
              <Landmark className="w-4 h-4" /> Maximum contribution $1,200 per person · Paid for by Graham of Delaware, LLC
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
